import { useMutation, useQueryClient } from "@tanstack/react-query";

interface UpdateGachaPrizePayload {
  userPrizeId: number;
  keep: 0 | 1; // 0 = unclip, 1 = clip
  appId: string;
  accessToken: string;
}

/**
 * Hook to update gacha prize keep status (clip/unclip)
 */
export function useUpdateGachaPrize() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: UpdateGachaPrizePayload) => {
      const { userPrizeId, keep, appId, accessToken } = payload;
      if (!appId) throw new Error("App ID is required");
      if (!accessToken) throw new Error("Access token is required");
      if (!userPrizeId) throw new Error("User Prize ID is required");

      const gachaApiUrl = process.env.NEXT_PUBLIC_GACHA_API_URL;

      if (!gachaApiUrl) {
        throw new Error("NEXT_PUBLIC_GACHA_API_URL is not configured");
      }
      const urlEncodedAppId = encodeURIComponent(appId);

      const response = await fetch(
        `${gachaApiUrl}api/app/welcia/user/prize/${userPrizeId}/update/keep`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            "Ocp-Apim-Subscription-Key":
              process.env.NEXT_PUBLIC_SUBSCRIPTION_KEY || "",
            Authorization: `Bearer ${accessToken}`,
          },
          body: `app_id=${urlEncodedAppId}&keep=${keep}`,
        },
      );

      if (!response.ok) {
        throw new Error(`Failed to update gacha prize: ${response.statusText}`);
      }

      return await response.json();
    },
    onSuccess: async (_data, variables) => {
      // Invalidate and refetch gacha offers after updating
      await queryClient.invalidateQueries({
        queryKey: ["gachaOffers", variables.appId],
      });
    },
  });
}
